/**
 * AdosScoringPanel — flat list of all ADOS-2 Module 4 items for the
 * ADOS-informed coding phase of Behavioural Observation.
 *
 * Layout depends on the Observation workflow version:
 *   • v1 (`taggingEnabled` false) — title on the left, four severity pills in a
 *     horizontal row on the right. No notes (pre-redesign behaviour).
 *   • v2 (`taggingEnabled` true) — three-column table matching the old Criterion
 *     Mapping look: item + mapping subtitle | vertically stacked score buttons |
 *     notes box. New notes auto-tag to the item's mapped DSM / behaviour section
 *     (`defaultTag` from adosConfig); clinicians can still amend via Tag.
 *
 * Purely presentational aside from the onAdosFieldChange callback that persists
 * scores and notes into observation.data.adosData.
 */

import React, { useMemo } from 'react';
import { ADOS_ALL_ITEMS, ADOS_DOMAINS } from '../config/adosConfig';
import { OBS_NOTE_TAG_LABEL_BY_ID } from '../config/obsNoteTagOptions';
import StructuredNotesEditor from './StructuredNotesEditor';

/**
 * Shared fixed width for ADOS severity pills (scoring buttons + read-only
 * summary badges). Sized so the longest label ("Significant impairment")
 * fits on one line — equal-width pills keep rows from reflowing.
 */
export const ADOS_SCORE_PILL_WIDTH_CLASS = 'w-[11.5rem]';

/** Severity colour coding for ADOS score buttons. */
const ADOS_SCORES = [
  { value: 0, label: 'No impairment',          selectedBg: 'bg-emerald-600', selectedBorder: 'border-emerald-600', hoverBorder: 'hover:border-emerald-400', rowBg: 'bg-emerald-50/50' },
  { value: 1, label: 'Mild impairment',        selectedBg: 'bg-amber-500',   selectedBorder: 'border-amber-500',   hoverBorder: 'hover:border-amber-400',   rowBg: 'bg-amber-50/50' },
  { value: 2, label: 'Significant impairment', selectedBg: 'bg-red-500',     selectedBorder: 'border-red-500',     hoverBorder: 'hover:border-red-400',     rowBg: 'bg-red-50/50' },
  { value: 3, label: 'Not applicable',         selectedBg: 'bg-gray-500',    selectedBorder: 'border-gray-500',    hoverBorder: 'hover:border-gray-400',    rowBg: 'bg-gray-50/50' },
];

/**
 * One severity button. Shared by the v1 horizontal row and the v2 vertical
 * stack so colour / toggle / accessibility stay identical.
 */
function AdosScoreButton({
  score,
  isSelected,
  readOnly,
  onSelect,
  /** When true, the button fills its column (v2 vertical stack). */
  fullWidth = false,
}) {
  // v2 vertical stack uses the same compact padding as Criterion Mapping
  // evidence pills (px-2.5 py-1, no min-height). v1 horizontal row keeps the
  // taller equal-width pills so labels stay aligned across the row.
  const sizeClass = fullWidth
    ? 'w-full px-2.5 py-1'
    : `${ADOS_SCORE_PILL_WIDTH_CLASS} min-h-[2.5rem] px-2.5 py-1`;

  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={() => {
        if (readOnly) return;
        onSelect();
      }}
      className={`
        inline-flex items-center justify-center
        ${sizeClass}
        rounded-lg text-xs font-semibold border
        text-center leading-tight whitespace-nowrap
        transition-all duration-150
        ${readOnly ? 'cursor-default' : ''}
        ${isSelected
          ? `${score.selectedBg} ${score.selectedBorder} text-white shadow-sm`
          : `bg-white text-gray-600 border-gray-300 ${readOnly ? '' : score.hoverBorder}`}
      `}
      title={score.label}
      aria-pressed={isSelected}
    >
      {score.label}
    </button>
  );
}

export default function AdosScoringPanel({ adosData, onAdosFieldChange, readOnly = false, taggingEnabled = false }) {
  const rowBgColor = (scoreValue) => {
    const score = ADOS_SCORES.find(s => s.value === scoreValue);
    return score ? score.rowBg : '';
  };

  const domainGroups = useMemo(() => {
    const groups = {};
    for (const item of ADOS_ALL_ITEMS) {
      if (!groups[item.domain]) groups[item.domain] = [];
      groups[item.domain].push(item);
    }
    return groups;
  }, []);

  return (
    // max-w + horizontal padding mirrors Final Formulation: readable column
    // with breathing room rather than edge-to-edge scoring rows.
    <div className="flex-grow w-full max-w-7xl mx-auto px-8 py-6 text-left">
      <div className="mb-6 text-left">
        <h1 className="text-gray-900 text-3xl font-bold">ADOS-Informed Coding</h1>
        <p className="text-gray-500 text-sm mt-1">
          Score each item based on observations during the assessment.
          {/* The per-item notes + auto-tagging affordance only exists in the
              v2 tagged-notes redesign; v1 patients score items only, exactly
              as the pre-redesign ADOS coding page did. */}
          {taggingEnabled && (
            <> Optional notes are auto-tagged to the matching DSM criterion — amend tags if needed.</>
          )}
        </p>
      </div>

      {Object.entries(domainGroups).map(([domainKey, items]) => (
        <div key={domainKey} className="mb-6">
          <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">
            {ADOS_DOMAINS[domainKey] || domainKey}
          </h2>

          {taggingEnabled ? (
            // v2 — three-column table (item | vertical scores | notes), same
            // visual language as the legacy Criterion Mapping contribution table.
            // overflow-visible so the Tag picker on notes is not clipped by the
            // card chrome (rounded corners stay via rounded-xl + thead radius).
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-primary text-white">
                    <th
                      className="px-4 py-3 text-sm font-bold text-left rounded-tl-xl"
                      style={{ width: '28%' }}
                    >
                      ADOS item
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-bold text-left"
                      style={{ width: '12.5rem' }}
                    >
                      Impairment
                    </th>
                    <th className="px-4 py-3 text-sm font-bold text-left rounded-tr-xl">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const scoreValue = adosData[`ados_${item.id}_score`];
                    const notes = adosData[`ados_${item.id}_notes`] || [];
                    const defaultTag = item.defaultTag;
                    const tagLabel = defaultTag
                      ? (OBS_NOTE_TAG_LABEL_BY_ID[defaultTag] || defaultTag)
                      : null;

                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-gray-200 last:border-b-0 transition-colors duration-300 ${rowBgColor(scoreValue)}`}
                      >
                        {/* Column 1 — ADOS line item + mapping subtitle */}
                        <td className="px-4 py-3 align-top">
                          <p className="text-sm text-gray-900 font-bold leading-snug">
                            {item.id}. {item.title}
                          </p>
                          {tagLabel && (
                            <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                              Notes auto-tag to{' '}
                              <span className="font-medium text-primary">{tagLabel}</span>
                            </p>
                          )}
                        </td>

                        {/* Column 2 — vertically stacked severity buttons */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-col gap-1.5">
                            {ADOS_SCORES.map((score) => (
                              <AdosScoreButton
                                key={score.value}
                                score={score}
                                isSelected={scoreValue === score.value}
                                readOnly={readOnly}
                                fullWidth
                                onSelect={() => onAdosFieldChange(
                                  `ados_${item.id}_score`,
                                  scoreValue === score.value ? null : score.value,
                                )}
                              />
                            ))}
                          </div>
                        </td>

                        {/* Column 3 — per-item notes (auto-tagged) */}
                        <td className="px-4 py-3 align-top overflow-visible">
                          <StructuredNotesEditor
                            notes={notes}
                            onChange={readOnly
                              ? undefined
                              : (val) => onAdosFieldChange(`ados_${item.id}_notes`, val)}
                            placeholder="Optional notes for this item — press Enter to save…"
                            readOnlyPlaceholder="No notes recorded"
                            readOnly={readOnly}
                            minHeight="7.5rem"
                            domainId={`ados_${item.id}`}
                            enableCriteriaTagging={!readOnly}
                            defaultCriteriaTags={defaultTag ? [defaultTag] : null}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            // v1 — original horizontal score-pill layout, no notes column.
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden divide-y divide-gray-200">
              {items.map((item) => {
                const scoreValue = adosData[`ados_${item.id}_score`];

                return (
                  <div
                    key={item.id}
                    className={`px-4 py-3 transition-colors duration-300 ${rowBgColor(scoreValue)}`}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                      <div className="min-w-0 text-left">
                        <p className="text-sm text-gray-900 font-semibold leading-snug">
                          {item.id}. {item.title}
                        </p>
                      </div>
                      {/*
                        Equal-width, single-line pills (widest label is
                        "Significant impairment") so scores stay aligned
                        and never collide when the left sidebar is expanded.
                      */}
                      <div className="inline-flex flex-nowrap items-stretch justify-end gap-2 flex-shrink-0">
                        {ADOS_SCORES.map((score) => (
                          <AdosScoreButton
                            key={score.value}
                            score={score}
                            isSelected={scoreValue === score.value}
                            readOnly={readOnly}
                            onSelect={() => onAdosFieldChange(
                              `ados_${item.id}_score`,
                              scoreValue === score.value ? null : score.value,
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
